import type {
  LogMethod,
  CreateMethodDto,
  UpdateMethodDto,
  DefaultMethodType,
} from "@repo/share-log-interfaces";
import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

// Schemas
const MethodSchema: z.ZodType<LogMethod> = z.object({
  id: z.number(),
  workspaceId: z.number(),
  defaultType: z
    .enum(["credit_card", "debit_card", "cash"])
    .nullable()
    .optional() as z.ZodType<DefaultMethodType | null | undefined>,
  name: z.string(),
  sortOrder: z.number(),
  color: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// Response Schemas
const CreateMethodResponseSchema: z.ZodType<DataAndMessageResponse<LogMethod>> =
  z.object({
    data: MethodSchema,
    message: z.string(),
  });

const GetMethodsResponseSchema: z.ZodType<DataAndMessageResponse<LogMethod[]>> =
  z.object({
    data: z.array(MethodSchema),
    message: z.string(),
  });

const UpdateMethodResponseSchema: z.ZodType<DataAndMessageResponse<LogMethod>> =
  z.object({
    data: MethodSchema,
    message: z.string(),
  });

const DeleteMethodResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

export function createMethodModule(httpClient: HttpClient) {
  return {
    create: async (workspaceId: number, data: CreateMethodDto) => {
      const response = await httpClient.post<typeof CreateMethodResponseSchema>(
        `/api/v1/workspaces/${workspaceId}/methods`,
        {
          data,
          schema: CreateMethodResponseSchema,
        },
      );
      return response.data;
    },

    list: async (workspaceId: number) => {
      const response = await httpClient.get<typeof GetMethodsResponseSchema>(
        `/api/v1/workspaces/${workspaceId}/methods`,
        {
          schema: GetMethodsResponseSchema,
        },
      );
      return response.data;
    },

    update: async (
      workspaceId: number,
      methodId: number,
      data: UpdateMethodDto,
    ) => {
      const response = await httpClient.patch<
        typeof UpdateMethodResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/methods/${methodId}`, {
        data,
        schema: UpdateMethodResponseSchema,
      });
      return response.data;
    },

    remove: async (workspaceId: number, methodId: number) => {
      const response = await httpClient.delete<
        typeof DeleteMethodResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/methods/${methodId}`, {
        schema: DeleteMethodResponseSchema,
      });
      return response;
    },
  };
}
